import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"
import "./weight_chart.css"

Chart.register(...registerables)

const TREND = "#6B7C5E"
const WEIGHT = "#4A6B8A"
const EXPECTED = "#B0405E"
const GOAL = "#A69F96"
const GRID = "#EDE7DC"

export default class extends Controller {
  static targets = ["canvas"]
  static values = { data: Object }

  connect() {
    const { labels, weights, trends, goal, expected } = this.dataValue
    const showWeights = this.pointRadius(weights.length) > 0

    const datasets = [
      {
        label: "Trend",
        data: trends,
        borderColor: TREND,
        backgroundColor: "rgba(107, 124, 94, 0.12)",
        borderWidth: 3,
        pointRadius: 0,
        tension: 0.3,
        fill: true,
        spanGaps: true
      },
      {
        label: "Weight",
        data: weights,
        borderColor: WEIGHT,
        showLine: false,
        pointRadius: this.pointRadius(weights.length),
        pointBorderWidth: 2,
        pointBackgroundColor: "#fff"
      }
    ]

    if (expected != null) {
      datasets.push({
        label: "Expected weight",
        data: expected,
        borderColor: EXPECTED,
        borderWidth: 2.5,
        borderDash: [6, 4],
        pointRadius: 0
      })
    }

    if (goal != null) {
      datasets.push({
        label: "Goal",
        data: labels.map(() => goal),
        borderColor: GOAL,
        borderWidth: 1.5,
        borderDash: [5, 4],
        pointRadius: 0
      })
    }

    this.chart = new Chart(this.canvasTarget, {
      type: "line",
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            position: "top",
            labels: { usePointStyle: true, boxHeight: 6 }
          },
          decimation: { enabled: true, algorithm: "lttb" }
        },
        scales: {
          x: { grid: { display: false }, ticks: { maxTicksLimit: 6 } },
          y: {
            grid: { color: GRID },
            // Scale to what's actually drawn: the trend line always, the
            // expected/goal lines when present, plus weight points only when
            // they're rendered. This keeps hidden weight outliers (large
            // ranges) from dominating the axis while keeping the goal in view.
            ...this.yBounds(trends, showWeights ? weights : [], expected ?? [])
          }
        }
      }
    })
  }

  disconnect() {
    this.chart?.destroy()
  }

  pointRadius(count) {
    if (count > 180) return 0
    if (count > 90) return 1.5
    return 3.5
  }

  // Symmetric padding around the min/max of the given series so the line
  // sits centered rather than pinned to the bottom of the chart.
  yBounds(...series) {
    const values = series.flat().filter((v) => v != null)
    if (values.length === 0) return {}

    const min = Math.min(...values)
    const max = Math.max(...values)
    const pad = (max - min) * 0.15 || 1

    return { min: min - pad, max: max + pad }
  }
}
